import React, { createContext, useContext, useMemo, useState } from "react";

// ── Types ─────────────────────────────────────────────────────────────────────

export type PeriodType = "daily" | "weekly" | "monthly" | "custom";

export interface CustomDateRange {
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
}

export interface DashboardPeriodContextValue {
  period: PeriodType;
  customRange: CustomDateRange | null;
  setPeriod: (p: PeriodType) => void;
  setCustomRange: (range: CustomDateRange) => void;
  resolvedDateFrom: string | undefined;
  resolvedDateTo: string | undefined;
}

// ── Context ───────────────────────────────────────────────────────────────────

const DashboardPeriodContext = createContext<DashboardPeriodContextValue | null>(
  null
);

// ── Helpers ───────────────────────────────────────────────────────────────────

function resolveWindow(
  period: PeriodType,
  customRange: CustomDateRange | null
): { from: string | undefined; to: string | undefined } {
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  if (period === "custom") {
    return {
      from: customRange?.from,
      to: customRange?.to,
    };
  }

  const to = fmt(today);
  const from = new Date(today);

  if (period === "daily") {
    from.setDate(today.getDate() - 6); // last 7 days
  } else if (period === "weekly") {
    from.setDate(today.getDate() - 27); // last 4 weeks
  } else {
    // monthly — last 12 months
    from.setMonth(today.getMonth() - 11);
    from.setDate(1);
  }

  return { from: fmt(from), to };
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function DashboardPeriodProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [period, setPeriodState] = useState<PeriodType>("monthly");
  const [customRange, setCustomRangeState] = useState<CustomDateRange | null>(
    null
  );

  const setPeriod = (p: PeriodType) => {
    setPeriodState(p);
    if (p !== "custom") {
      setCustomRangeState(null);
    }
  };

  const setCustomRange = (range: CustomDateRange) => {
    if (range.from <= range.to) {
      setCustomRangeState(range);
      setPeriodState("custom");
    }
  };

  const { from: resolvedDateFrom, to: resolvedDateTo } = useMemo(
    () => resolveWindow(period, customRange),
    [period, customRange]
  );

  const value: DashboardPeriodContextValue = {
    period,
    customRange,
    setPeriod,
    setCustomRange,
    resolvedDateFrom,
    resolvedDateTo,
  };

  return (
    <DashboardPeriodContext.Provider value={value}>
      {children}
    </DashboardPeriodContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useDashboardPeriod(): DashboardPeriodContextValue {
  const ctx = useContext(DashboardPeriodContext);
  if (!ctx) {
    throw new Error(
      "useDashboardPeriod must be used inside DashboardPeriodProvider"
    );
  }
  return ctx;
}
